import { environment } from '@/env/environment';
import { session } from './session';

const ANONYMOUS_IDENTITY = new Set([
  'login',
  'firebase-login',
  'refresh',
  'register',
  'forgot-password',
  'reset-password',
  'verify-email',
]);

/** Sign-in and recovery calls: a refusal there is an answer, not a lost session. */
function isAnonymousIdentity(url: string): boolean {
  const base = new URL(environment.apiBaseUrl, window.location.origin);
  const target = new URL(url, window.location.origin);
  if (target.origin !== base.origin) return false;
  const segments = target.pathname.toLowerCase().split('/').filter(Boolean);
  return (
    segments.length === 3 &&
    segments[0] === 'api' &&
    segments[1] === 'identity' &&
    ANONYMOUS_IDENTITY.has(segments[2]!)
  );
}

let refreshing: Promise<string | null> | null = null;

/** Rotates the tokens once for every concurrent caller. Resolves to the new access token, or null. */
export function refreshAccessToken(): Promise<string | null> {
  if (refreshing) return refreshing;
  const current = session.get();
  if (!current) return Promise.resolve(null);
  const epoch = session.epoch();

  refreshing = (async () => {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/api/identity/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });
      if (session.epoch() !== epoch) return null;
      if (!response.ok) {
        if (response.status === 400 || response.status === 401) session.end('expired');
        return null;
      }
      const tokens = (await response.json()) as { accessToken: string; refreshToken: string };
      if (session.epoch() !== epoch) return null;
      session.rotate(tokens.accessToken, tokens.refreshToken);
      return tokens.accessToken;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function withToken(request: Request, token: string | undefined): Request {
  if (!token) return request;
  const authorized = new Request(request);
  authorized.headers.set('Authorization', `Bearer ${token}`);
  return authorized;
}

/**
 * fetch with the session's bearer token. A 401 refreshes and retries once; a 403 does too,
 * because roles and organization claims live in the token and go stale after an approval.
 */
export async function authFetch(request: Request): Promise<Response> {
  if (isAnonymousIdentity(request.url)) return fetch(request);

  const retry = request.clone();
  const response = await fetch(withToken(request, session.get()?.accessToken));
  if ((response.status !== 401 && response.status !== 403) || !session.get()) return response;

  const token = await refreshAccessToken();
  if (!token) {
    if (response.status === 401) session.end('expired');
    return response;
  }
  const second = await fetch(withToken(retry, token));
  if (second.status === 401) session.end('expired');
  return second;
}
